// Comparación informativa entre los kilos acordados en una compra PREVIA
// y el peso NETO recibido (peso bruto − tara de envases − peso de pallet).
// No altera montos, inventario, facturas ni gastos.
//
// Diferencia (kg) = peso neto recibido − acordados.
// Diferencia (%)  = diferencia / acordados × 100.
// Se calcula en milésimas enteras para evitar errores de coma flotante.

const ESTADOS_DEFINITIVOS = ['VALIDADA', 'RECHAZADA_TOTAL']

function aMilesimas(valor) {
  if (valor === null || valor === undefined || valor === '') return null

  const numero = Number(valor)

  if (!Number.isFinite(numero)) return null

  return Math.round(numero * 1000)
}

export const RESULTADOS_DIFERENCIA = {
  faltante: {
    etiqueta: 'Faltante',
    clase: 'bg-red-100 text-red-700',
    claseTexto: 'text-red-700',
    claseCaja: 'border-red-200 bg-red-50',
  },
  excedente: {
    etiqueta: 'Excedente',
    clase: 'bg-amber-100 text-amber-800',
    claseTexto: 'text-amber-800',
    claseCaja: 'border-amber-200 bg-amber-50',
  },
  coincide: {
    etiqueta: 'Coincide',
    clase: 'bg-emerald-100 text-emerald-700',
    claseTexto: 'text-emerald-700',
    claseCaja: 'border-emerald-200 bg-emerald-50',
  },
}

export function formatearKilosDiferencia(valor, { conSigno = false } = {}) {
  const numero = Number(valor)

  if (!Number.isFinite(numero)) return '—'

  const texto = `${Math.abs(numero).toLocaleString('es-CL', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 3,
  })} kg`

  if (!conSigno || numero === 0) return texto

  return `${numero < 0 ? '−' : '+'}${texto}`
}

export function formatearPorcentajeDiferencia(valor, { conSigno = true } = {}) {
  const numero = Number(valor)

  if (!Number.isFinite(numero)) return '—'

  const texto = `${Math.abs(numero).toLocaleString('es-CL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} %`

  if (!conSigno || numero === 0) return texto

  return `${numero < 0 ? '−' : '+'}${texto}`
}

// estadoRecepcion: null/undefined (sin recepción), 'BORRADOR',
// 'VALIDADA' o 'RECHAZADA_TOTAL'.
// netoKg: peso neto recibido (en recepción en curso, la suma de los netos
// del formulario). Es el único valor que se compara con lo acordado.
// brutoKg / taraKg: solo informativos, no intervienen en la diferencia.
// aceptadosKg: solo aplica a recepciones definitivas.
export function calcularDiferenciaKilos({
  modalidad,
  acordadoKg,
  brutoKg,
  netoKg,
  taraKg,
  aceptadosKg,
  estadoRecepcion,
  enEdicion = false,
}) {
  if (modalidad !== 'PREVIA') {
    return { aplica: false, estado: 'no_aplica' }
  }

  const acordado = aMilesimas(acordadoKg)
  const bruto = aMilesimas(brutoKg)
  const neto = aMilesimas(netoKg)
  const tara = aMilesimas(taraKg)

  const base = {
    aplica: true,
    acordadoKg: acordado === null ? null : acordado / 1000,
  }

  // Sin recepción (y sin datos en edición) no se inventa un faltante.
  if (!estadoRecepcion && !enEdicion) {
    return { ...base, estado: 'pendiente' }
  }

  if (acordado === null || acordado <= 0 || neto === null || neto <= 0) {
    return { ...base, estado: 'pendiente' }
  }

  const diferencia = neto - acordado
  const definitivo =
    !enEdicion && ESTADOS_DEFINITIVOS.includes(estadoRecepcion)
  const aceptados = aMilesimas(aceptadosKg)

  return {
    ...base,
    estado: definitivo ? 'definitivo' : 'preliminar',
    resultado:
      diferencia < 0 ? 'faltante' : diferencia > 0 ? 'excedente' : 'coincide',
    brutoKg: bruto === null ? null : bruto / 1000,
    taraKg: tara === null ? null : tara / 1000,
    netoKg: neto === null ? null : neto / 1000,
    diferenciaKg: diferencia / 1000,
    porcentaje: Math.round((diferencia * 10000) / acordado) / 100,
    aceptadosKg: definitivo && aceptados !== null ? aceptados / 1000 : null,
    rechazoTotal: definitivo && estadoRecepcion === 'RECHAZADA_TOTAL',
  }
}

export function describirDiferencia(comparacion) {
  if (!comparacion?.aplica) return ''

  if (comparacion.estado === 'pendiente') return 'Pendiente de recepción'

  const kilos = formatearKilosDiferencia(comparacion.diferenciaKg)

  if (comparacion.resultado === 'faltante') {
    return `Faltaron ${kilos} respecto de lo acordado`
  }

  if (comparacion.resultado === 'excedente') {
    return `Llegaron ${kilos} adicionales respecto de lo acordado`
  }

  return 'Coincide con lo acordado'
}
